import { type FC, type ReactNode, useCallback, useContext, useEffect, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useToken } from "@packsize/authentication";
import { isEqual } from "lodash";

import { ProductionContext } from "@/contexts/production";
import { useSignalRHub } from "@/hooks/use-signalr-hub";
import { baseRoutes } from "@/api/base-routes";
import { aliasSort } from "@/utils/sort";
import * as productionGroupApi from "@/api/selection/production-group-api";
import { type MachineGroup } from "@/schemas/machine-group";
import { type ProductionGroup } from "@/schemas/production-group";

import { ProductionGroupContext } from "./production-group.context";

const productionGroupsQueryKey = ["productionGroups"];

interface ProductionGroupProviderProps {
	readonly children: ReactNode;
}

export const ProductionGroupProvider: FC<ProductionGroupProviderProps> = ({ children }) => {
	const { bearerToken } = useToken();
	const queryClient = useQueryClient();

	const { decodedToken } = useToken();

	const { hubConnection, usePolling } = useSignalRHub({
		hubUrl: `${baseRoutes.selectionHub}/batches`,
		token: bearerToken,
	});

	const {
		currentMachineGroup,
		currentProductionGroup,
		productionGroups,
		setProductionGroups,
		setCurrentProductionGroup,
	} = useContext(ProductionContext);

	// Wiring up callbacks for when ProductionGroups publishes messages
	useEffect(() => {
		if (!hubConnection || !decodedToken) return;

		hubConnection.on("OnProductionGroupsVersion", (version) => {
			console.info(
				`signalr ProductionGroup OnProductionGroupsVersion for connectionid ${hubConnection.connectionId}- ${version}`,
			);
		});

		hubConnection.on("OnProductionGroupUpdated", (tenantId, productionGroup) => {
			if (tenantId !== decodedToken.tid) return;

			const oldProductionGroups = productionGroups.filter((pg) => pg.id !== productionGroup.id);
			const newProductionGroups = [...oldProductionGroups, productionGroup];
			newProductionGroups.sort(aliasSort);
			setProductionGroups(newProductionGroups);
		});

		hubConnection.on("OnProductionGroupAdded", (tenantId, productionGroup) => {
			if (tenantId !== decodedToken.tid) return;

			const newProductionGroups = [...productionGroups, productionGroup];
			newProductionGroups.sort(aliasSort);
			setProductionGroups(newProductionGroups);
		});

		hubConnection.on("OnProductionGroupDeleted", (tenantId, productionGroupId) => {
			if (tenantId !== decodedToken.tid) return;

			const newProductionGroups = productionGroups.filter((pg) => pg.id !== productionGroupId);
			setProductionGroups(newProductionGroups);
		});

		// Cleanup block
		return () => {
			hubConnection.off("OnProductionGroupsVersion");
			hubConnection.off("OnProductionGroupUpdated");
			hubConnection.off("OnProductionGroupAdded");
			hubConnection.off("OnProductionGroupDeleted");
		};
	}, [hubConnection, productionGroups, decodedToken, setProductionGroups]);

	useEffect(() => {
		if (productionGroups.length > 0 && currentMachineGroup) {
			const newCurrentPg = productionGroups.find((pg) => pg.configuredMachineGroups.includes(currentMachineGroup.id));

			if (!isEqual(newCurrentPg, currentProductionGroup)) {
				setCurrentProductionGroup(newCurrentPg ?? null);
			}
		}
	}, [productionGroups, currentMachineGroup, currentProductionGroup, setCurrentProductionGroup]);

	const queryFunction = async () => {
		const pgs = await productionGroupApi.getProductionGroups();

		const sortedPgs = pgs.sort(aliasSort);
		setProductionGroups(sortedPgs);

		return sortedPgs;
	};

	useQuery({
		queryKey: productionGroupsQueryKey,
		queryFn: queryFunction,
		refetchInterval: usePolling ? 3000 : false,
		enabled: Boolean(bearerToken),
	});

	const changeProductionGroup = useCallback(
		async (mg: MachineGroup, pg: ProductionGroup) => {
			if (!mg.id || !pg.id) {
				return;
			}

			const response = await productionGroupApi.assignMachineGroupToProductionGroup(pg.id, mg.id);
			setCurrentProductionGroup(response.data);
			queryClient.invalidateQueries({ queryKey: productionGroupsQueryKey });
		},
		[queryClient, setCurrentProductionGroup],
	);

	const removeMachineGroupFromProductionGroup = useCallback(
		async (mg: MachineGroup) => {
			if (!mg.id) {
				return;
			}

			await productionGroupApi.removeMachineGroupFromProductionGroup(mg.id);

			queryClient.invalidateQueries({ queryKey: productionGroupsQueryKey });
			setCurrentProductionGroup(null);
		},
		[queryClient, setCurrentProductionGroup],
	);

	const value = useMemo(
		() => ({
			changeProductionGroup,
			removeMachineGroupFromProductionGroup,
		}),
		[changeProductionGroup, removeMachineGroupFromProductionGroup],
	);

	return <ProductionGroupContext.Provider value={value}>{children}</ProductionGroupContext.Provider>;
};
